/**
 * Area-code pictogram registry.
 *
 * Maps every canonical `AreaCode` from `shared/schemas.ts` to a Lucide icon so
 * the rest of the FE can render an area pictogram via a single import:
 *
 *   import { AREA_ICON, pictogramFor } from '@/components/pictograms'
 *   const Icon = pictogramFor('vehicle_mtpl')   // returns Car
 *
 * Unmapped codes fall back to `ScrollText` (a generic "contract" glyph) — the
 * fallback exists because the wiki Domain-Glossary may add new codes ahead of
 * this module. The mapping is intentionally semantic, not visual: e.g.
 * `liability_personal` maps to `Users` because the rule covers everyday
 * third-party harm including children and pets, not because the icon depicts
 * liability literally.
 */
import {
  Accessibility,
  Activity,
  Backpack,
  Bandage,
  Banknote,
  Briefcase,
  Building,
  Car,
  Cat,
  CloudRain,
  Dog,
  FileText,
  Gavel,
  GlobeLock,
  HardHat,
  HeartPulse,
  Home,
  Hospital,
  Lock,
  Luggage,
  Mountain,
  Plane,
  Receipt,
  Scale,
  ScrollText,
  Shield,
  Snowflake,
  Sofa,
  Stethoscope,
  TrendingDown,
  Truck,
  Umbrella,
  Users,
  Wallet,
  Wind,
  type LucideIcon,
} from 'lucide-react'

import type { AreaCode } from '@shared/schemas'

// Re-export `AreaCode` so consumers can write
// `import type { AreaCode } from '@/components/pictograms'` without reaching
// into `@shared/schemas` directly when all they need is the icon lookup.
export type { AreaCode }

/**
 * Semantic icon for each canonical insurance area.
 *
 * Selection rationale, grouped by zone:
 *
 * - 🏠 Property: building structure → `Building`; home contents → `Sofa`;
 *   glass → `Wind` (closest in-set glyph for "windows / fragility"); theft → `Lock`.
 * - 🚗 Vehicle: MTPL (legally required) → `Car`; casco → `Shield`;
 *   glass → `Wind`; assistance → `Truck` (tow); GAP → `TrendingDown` (value drop).
 * - 👤 Personal: life → `HeartPulse`; accident → `Bandage`;
 *   invalidity → `Accessibility`; sick leave → `Activity`;
 *   critical illness → `Stethoscope`; hospital daily → `Hospital`;
 *   supplemental health → `Cross` is misleading (religious overload), so `Activity` family — using `Activity` already; reach for `Stethoscope` for `health_supplemental`.
 * - 👨‍👩‍👧‍👦 Family: liability → `Users`; child accident → `Bandage`;
 *   mortgage → `Home`.
 * - 💼 Work: employee liability → `HardHat`; professional liability → `Briefcase`;
 *   unemployment → `Wallet`.
 * - ✈️ Travel: travel → `Plane`; cancellation → `Receipt`;
 *   luggage → `Luggage`; sport → `Mountain`.
 * - 🐾 Pet: pet → `Dog`; pet liability → `Cat` (visual distinction from `pet`).
 * - 🔒 Cross-cutting: legal → `Gavel`; cyber → `GlobeLock`.
 */
export const AREA_ICON: Record<AreaCode, LucideIcon> = {
  // 🏠 Dům (property)
  property_building: Building,
  property_home: Sofa,
  property_glass: Wind,
  property_theft: Lock,
  // 🚗 Auto (vehicle)
  vehicle_mtpl: Car,
  vehicle_casco: Shield,
  vehicle_glass: Wind,
  vehicle_assistance: Truck,
  vehicle_gap: TrendingDown,
  // 👤 Osoba (personal)
  life: HeartPulse,
  accident: Bandage,
  disability_invalidity: Accessibility,
  sick_leave: Activity,
  critical_illness: Stethoscope,
  hospital_daily: Hospital,
  health_supplemental: Stethoscope,
  // 👨‍👩‍👧‍👦 Rodina (family)
  liability_personal: Users,
  child_accident: Backpack,
  mortgage_protection: Home,
  // 💼 Práce (work)
  liability_employee: HardHat,
  liability_professional: Briefcase,
  unemployment: Wallet,
  // ✈️ Dovolená (travel)
  travel: Plane,
  travel_cancellation: Receipt,
  travel_luggage: Luggage,
  travel_sport: Mountain,
  // 🐾 Mazlíček (pet) — not a demo zone but kept for data completeness
  pet: Dog,
  pet_liability: Cat,
  // 🔒 Ostatní (cross-cutting)
  legal_protection: Gavel,
  cyber: GlobeLock,
}

/**
 * Default icon for any code not present in `AREA_ICON`. Exported so consumers
 * can use it directly when they need a "generic insurance contract" glyph
 * (e.g. an unknown contract type returned from a future BE).
 */
export const DEFAULT_AREA_ICON: LucideIcon = ScrollText

/**
 * Lookup helper that always returns an icon — never `undefined`.
 *
 * Prefer this over indexing `AREA_ICON` directly when the input might be a
 * loosely-typed string from a fixture, route param, or future API response.
 */
export function pictogramFor(code: AreaCode | string | undefined): LucideIcon {
  if (!code) return DEFAULT_AREA_ICON
  return (AREA_ICON as Record<string, LucideIcon>)[code] ?? DEFAULT_AREA_ICON
}

/**
 * Re-exports of the small set of non-area icons the wider FE needs (used by
 * recommendation severity rings, confirm checkmarks, etc.). Grouping them here
 * keeps the "import from one place" promise from the build plan.
 */
export {
  // Severity / state
  Shield, // general "covered"
  // Documents
  FileText,
  ScrollText,
  // Money
  Banknote,
  // Misc atmosphere (sometimes used as recommendation kind hints)
  CloudRain,
  Snowflake,
  Umbrella,
  Scale,
}
